/**
 * Loads images with bounded concurrency and remembers outcomes:
 * - successes stay decoded in an LRU capped by estimated bitmap bytes and
 *   count, so scrolling back does not re-decode;
 * - failures are remembered (bounded), so dead logos, which are common in
 *   IPTV catalogs, are not requested again on every scroll.
 * Requests for rows scrolled out of view can be cancelled before they start.
 */
export interface ImageLoaderOptions {
  maxConcurrent?: number;
  maxBytes?: number;
  maxEntries?: number;
  maxFailures?: number;
  timeoutMs?: number;
  createImage?: () => HTMLImageElement;
}

export type ImageStatus = 'loaded' | 'failed' | 'unknown';

interface Job {
  url: string;
  waiters: Set<(ok: boolean) => void>;
  started: boolean;
}

export class ImageLoader {
  private readonly maxConcurrent: number;
  private readonly maxBytes: number;
  private readonly maxEntries: number;
  private readonly maxFailures: number;
  private readonly timeoutMs: number;
  private readonly createImage: () => HTMLImageElement;
  private readonly loaded = new Map<string, { image: HTMLImageElement; bytes: number }>();
  private readonly failed = new Set<string>();
  private readonly jobs = new Map<string, Job>();
  private readonly queue: Job[] = [];
  private active = 0;
  private bytes = 0;

  constructor(options: ImageLoaderOptions = {}) {
    this.maxConcurrent = options.maxConcurrent ?? 6;
    this.maxBytes = options.maxBytes ?? 48 * 1024 * 1024;
    this.maxEntries = options.maxEntries ?? 400;
    this.maxFailures = options.maxFailures ?? 5000;
    this.timeoutMs = options.timeoutMs ?? 15_000;
    this.createImage = options.createImage ?? (() => new Image());
  }

  status(url: string): ImageStatus {
    if (this.loaded.has(url)) {
      this.touch(url);
      return 'loaded';
    }
    return this.failed.has(url) ? 'failed' : 'unknown';
  }

  get cachedBytes(): number {
    return this.bytes;
  }

  get cachedCount(): number {
    return this.loaded.size;
  }

  /** Resolves with the outcome; the returned function cancels interest. */
  load(url: string, done: (ok: boolean) => void): () => void {
    const known = this.status(url);
    if (known !== 'unknown') {
      done(known === 'loaded');
      return () => undefined;
    }
    let job = this.jobs.get(url);
    if (!job) {
      job = { url, waiters: new Set(), started: false };
      this.jobs.set(url, job);
      this.queue.push(job);
    }
    job.waiters.add(done);
    this.pump();
    const current = job;
    return () => {
      current.waiters.delete(done);
      if (current.waiters.size === 0 && !current.started) {
        this.jobs.delete(url);
        const index = this.queue.indexOf(current);
        if (index !== -1) this.queue.splice(index, 1);
      }
    };
  }

  private pump(): void {
    while (this.active < this.maxConcurrent) {
      // Newest requests first: they belong to rows that are on screen now.
      const job = this.queue.pop();
      if (!job) return;
      this.start(job);
    }
  }

  private start(job: Job): void {
    job.started = true;
    this.active++;
    const image = this.createImage();
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      this.active--;
      this.jobs.delete(job.url);
      if (ok) this.remember(job.url, image);
      else this.rememberFailure(job.url);
      for (const waiter of job.waiters) waiter(ok);
      this.pump();
    };
    const timer = setTimeout(() => {
      image.src = '';
      finish(false);
    }, this.timeoutMs);
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.decoding = 'async';
    image.src = job.url;
  }

  private remember(url: string, image: HTMLImageElement): void {
    const bytes = Math.max(1, image.naturalWidth * image.naturalHeight * 4);
    this.loaded.set(url, { image, bytes });
    this.bytes += bytes;
    for (const [key, entry] of this.loaded) {
      if (this.bytes <= this.maxBytes && this.loaded.size <= this.maxEntries) break;
      if (key === url) continue;
      this.loaded.delete(key);
      this.bytes -= entry.bytes;
    }
  }

  private rememberFailure(url: string): void {
    this.failed.add(url);
    if (this.failed.size > this.maxFailures) {
      const oldest = this.failed.values().next().value;
      if (oldest !== undefined) this.failed.delete(oldest);
    }
  }

  private touch(url: string): void {
    const entry = this.loaded.get(url);
    if (!entry) return;
    this.loaded.delete(url);
    this.loaded.set(url, entry);
  }
}
